import isEqual from 'lodash/isEqual'

// Job alerts inside a workflow (SPT-2316). Stored on the cadence as `settings.job_alerts`:
//   { enabled, frequency: 'immediately' | 'daily' | 'weekly', channels: ['email' | 'sms' | 'push' | 'in_app_message'],
//     templates: { email?: <template id>, sms?: <template id>, push?: <template id>, in_app_message?: <template id> } }
// Job alerts run ALONGSIDE the steps while a candidate is enrolled, and stop when they finish or leave.
// Every other `settings` key is left exactly as loaded.

export const JOB_ALERTS_KEY = 'job_alerts'

export const JOB_ALERTS_HELP = 'Send new qualified job matches to candidates in this workflow. The pilot, daily limit, quiet hours and opt-outs apply here too.'

export const JOB_ALERT_FREQUENCIES = ['immediately', 'daily', 'weekly']
export const JOB_ALERT_DEFAULT_FREQUENCY = 'daily'
export const JOB_ALERT_FREQUENCY_LABELS = { immediately: 'As each new match is found', daily: 'Daily', weekly: 'Weekly' }

export const JOB_ALERT_CHANNELS = ['email', 'sms', 'push', 'in_app_message']
export const JOB_ALERT_CHANNEL_LABELS = { email: 'Email', sms: 'SMS', push: 'Push notification', in_app_message: 'In-app message' }
export const JOB_ALERT_CHANNEL_HINTS = {
	email: 'Up to 3 new matches in one email.',
	sms: 'Only to candidates who opted in to texts, only on a day with a new match.',
	push: 'To candidates with the app installed and notifications allowed.',
	in_app_message: "Sent as a message in the candidate's inbox.",
}
export const JOB_ALERTS_IN_APP_OFF = 'In-app messages are off for this account. The saved in-app message choice is kept.'

// Codes the API's publish validator reports for this section (cadence.validation.issues).
export const JOB_ALERT_ISSUE_CODES = [
	'job_alerts_no_channel',
	'job_alerts_template_unbound',
	'job_alerts_template_channel_mismatch',
	'job_alerts_in_app_unavailable',
]
export const isJobAlertsIssue = (item) => JOB_ALERT_ISSUE_CODES.includes(item?.code)

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

// The stored value, read defensively. Unknown keys inside `job_alerts` are carried along untouched.
export const readJobAlerts = (settings) => {
	const raw = isObject(settings?.[JOB_ALERTS_KEY]) ? settings[JOB_ALERTS_KEY] : {}
	const channels = Array.isArray(raw.channels) ? raw.channels.filter((channel) => typeof channel === 'string') : []
	return {
		...raw,
		enabled: raw.enabled === true,
		frequency: typeof raw.frequency === 'string' && raw.frequency ? raw.frequency : JOB_ALERT_DEFAULT_FREQUENCY,
		channels: [...new Set(channels)],
		templates: isObject(raw.templates) ? raw.templates : {},
	}
}

export const hasJobAlerts = (settings) => isObject(settings?.[JOB_ALERTS_KEY])

// Merge, never replace: the rest of `settings` is round-tripped as loaded.
export const writeJobAlerts = (settings, jobAlerts) => ({ ...(settings || {}), [JOB_ALERTS_KEY]: jobAlerts })

export const setJobAlertsEnabled = (settings, enabled) => {
	const current = readJobAlerts(settings)
	// First switch-on: Daily by email, the least surprising start.
	const channels = enabled && !hasJobAlerts(settings) ? ['email'] : current.channels
	return writeJobAlerts(settings, { ...current, enabled, channels })
}

export const setJobAlertsFrequency = (settings, frequency) => writeJobAlerts(settings, { ...readJobAlerts(settings), frequency })

// Channels keep the canonical order; unticking a channel drops its template so the stored value stays clean.
export const toggleJobAlertsChannel = (settings, channel, checked) => {
	const current = readJobAlerts(settings)
	const selected = new Set(current.channels)
	if (checked) selected.add(channel)
	else selected.delete(channel)
	const channels = [...JOB_ALERT_CHANNELS.filter((item) => selected.has(item)), ...current.channels.filter((item) => !JOB_ALERT_CHANNELS.includes(item))]
	const templates = { ...current.templates }
	if (!checked) delete templates[channel]
	return writeJobAlerts(settings, { ...current, channels, templates })
}

export const setJobAlertsTemplate = (settings, channel, templateId) => {
	const current = readJobAlerts(settings)
	const templates = { ...current.templates }
	if (templateId) templates[channel] = templateId
	else delete templates[channel]
	return writeJobAlerts(settings, { ...current, templates })
}

// Templates a channel's picker offers. `channelsKnown` is false when the API has no `channel` field on
// templates: every template there is an email template, and SMS / in-app templates cannot be told apart.
export const templatesForChannel = (templates, channel, { channelsKnown }) => {
	const list = (templates || []).filter((item) => item?.id)
	if (channelsKnown) return list.filter((item) => (item.channel || 'email') === channel)
	return channel === 'email' ? list : []
}

const issue = (code, message) => ({ code, severity: 'error', message, pathKey: null, stepKey: null, branchKey: null })

// Client mirror of the API's job alert checks, so they show while editing (graphIssues).
export const jobAlertsIssues = (settings) => {
	const jobAlerts = readJobAlerts(settings)
	if (!jobAlerts.enabled) return []
	if (!jobAlerts.channels.length) return [issue('job_alerts_no_channel', 'Job alerts: choose at least one communication type.')]
	return jobAlerts.channels
		.filter((channel) => JOB_ALERT_CHANNELS.includes(channel) && !jobAlerts.templates[channel])
		.map((channel) => issue('job_alerts_template_unbound', `Job alerts: choose a template for ${JOB_ALERT_CHANNEL_LABELS[channel]}.`))
}

// What the section lists under itself. The API's issues describe the SAVED workflow, so they are shown
// (with their own message) only while the job alert draft still equals what was saved; the client mirror
// covers anything the API did not report, and everything once the draft has changed.
export const jobAlertsSectionIssues = (settings, { savedSettings, serverIssues } = {}) => {
	const client = jobAlertsIssues(settings)
	if (!isEqual(readJobAlerts(settings), readJobAlerts(savedSettings))) return client
	const server = (Array.isArray(serverIssues) ? serverIssues : []).filter(isJobAlertsIssue)
	return [...server, ...client.filter((item) => !server.some((reported) => reported.code === item.code))]
}

export const jobAlertsFrequencyLabel = (frequency) => JOB_ALERT_FREQUENCY_LABELS[frequency] || String(frequency || '')

export const jobAlertsChannelsLabel = (channels) => (channels || []).map((channel) => JOB_ALERT_CHANNEL_LABELS[channel] || channel).join(', ')

// "Daily · Email, SMS": the review-changes line.
export const describeJobAlerts = (settings) => {
	const jobAlerts = readJobAlerts(settings)
	if (!jobAlerts.enabled) return 'Off'
	const channels = jobAlertsChannelsLabel(jobAlerts.channels) || 'No communication type chosen'
	return `${jobAlertsFrequencyLabel(jobAlerts.frequency)} · ${channels}`
}

// A workflow with job alerts and no message step (the "New job alerts" preset) keeps its candidates enrolled
// instead of finishing them at the end of the path, as the API does (Cadence#job_alerts_only?).
export const MESSAGE_STEP_KINDS = ['email', 'sms', 'push']
const hasMessageStep = (steps) =>
	(steps || []).some((step) => MESSAGE_STEP_KINDS.includes(step.kind) || (step.branches || []).some((branch) => hasMessageStep(branch.steps)))
export const isJobAlertsOnly = (graph) => readJobAlerts(graph?.settings).enabled && !(graph?.paths || []).some((path) => hasMessageStep(path.steps))

// The rail's "runs alongside" card: when job alerts run, and when they stop.
export const JOB_ALERTS_RAIL_TITLE = 'While a candidate is in this workflow: new qualified job matches'
// `templates` (the tenant's templates, when loaded) names the template each channel sends: "Email: New jobs for you".
export const describeJobAlertsRail = (settings, { alertsOnly = false, templates = null } = {}) => {
	const jobAlerts = readJobAlerts(settings)
	const named = (channel) => {
		const label = JOB_ALERT_CHANNEL_LABELS[channel] || channel
		if (!Array.isArray(templates)) return label
		const template = templates.find((item) => item?.id === jobAlerts.templates[channel])
		return `${label}: ${template ? `“${template.name}”` : 'no template chosen'}`
	}
	const channels = jobAlerts.channels.length ? jobAlerts.channels.map(named).join(', ') : 'No communication type chosen'
	const end = alertsOnly ? 'Candidates stay in this workflow while they match the audience.' : 'Stops when they finish or leave.'
	return `${jobAlertsFrequencyLabel(jobAlerts.frequency)} · ${channels}. ${end}`
}
